/**
 * ゲーム設定（酔い対策と音量）。
 * localStorage に保存し、次回起動時に復元する。
 */

import { DEFAULT_VOLUMES, type VolumeSettings } from '../audio/AudioManager';
import { loadJson, saveJson, SETTINGS_KEY } from '../scenario/state';

export interface GameSettings {
  /** 視野角（度）。広いほど酔いにくい人と、狭いほうが酔いにくい人がいるため調整可能にする */
  fov: number;
  /** マウス感度 */
  mouseSensitivity: number;
  /** 歩行時の上下の揺れ。酔いやすい人は OFF にできる */
  headBob: boolean;
  /** 視点移動のなめらかさ（0 = 即応、1 = ゆるやか） */
  viewSmoothing: number;
  volumes: VolumeSettings;
}

export const DEFAULT_SETTINGS: GameSettings = {
  fov: 75,
  mouseSensitivity: 1.0,
  headBob: false,
  viewSmoothing: 0.25,
  volumes: { ...DEFAULT_VOLUMES },
};

export const SETTINGS_RANGES = {
  fov: { min: 60, max: 100, step: 1 },
  mouseSensitivity: { min: 0.2, max: 3.0, step: 0.1 },
  viewSmoothing: { min: 0, max: 0.8, step: 0.05 },
} as const;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function normalizeSettings(input: Partial<GameSettings> | null): GameSettings {
  const base = { ...DEFAULT_SETTINGS, ...(input ?? {}) };
  return {
    fov: clamp(Number(base.fov) || DEFAULT_SETTINGS.fov, SETTINGS_RANGES.fov.min, SETTINGS_RANGES.fov.max),
    mouseSensitivity: clamp(
      Number(base.mouseSensitivity) || DEFAULT_SETTINGS.mouseSensitivity,
      SETTINGS_RANGES.mouseSensitivity.min,
      SETTINGS_RANGES.mouseSensitivity.max,
    ),
    headBob: !!base.headBob,
    viewSmoothing: clamp(
      Number(base.viewSmoothing ?? DEFAULT_SETTINGS.viewSmoothing),
      SETTINGS_RANGES.viewSmoothing.min,
      SETTINGS_RANGES.viewSmoothing.max,
    ),
    volumes: {
      master: clamp(Number(base.volumes?.master ?? DEFAULT_VOLUMES.master), 0, 1),
      bgm: clamp(Number(base.volumes?.bgm ?? DEFAULT_VOLUMES.bgm), 0, 1),
      sfx: clamp(Number(base.volumes?.sfx ?? DEFAULT_VOLUMES.sfx), 0, 1),
      ambient: clamp(Number(base.volumes?.ambient ?? DEFAULT_VOLUMES.ambient), 0, 1),
    },
  };
}

export function loadSettings(): GameSettings {
  return normalizeSettings(loadJson<Partial<GameSettings>>(SETTINGS_KEY));
}

export function persistSettings(settings: GameSettings): void {
  saveJson(SETTINGS_KEY, settings);
}
